import axios from 'axios'
import qs from 'qs'
import Login from './login'
import { getCommonParams, sendRaptorErr } from './util'
import toast from '../components/toast/index'
import env from './env'

// 创建一个axios实例，设置基本配置
const Ajax = axios.create({
  baseURL: process.env.VUE_APP_SERVER_URL || 'https://quickpass.meituan.com',
  timeout: 6000,
  method: 'post',
  withCredentials: true,
  headers: {
    'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
  },
})

// 🟩 【新增】MSI 实例缓存（仅在鸿蒙系统中）
let msiInstance = null
let msiLoadPromise = null

/**
 * 获取 MSI 实例
 * 参考：https://km.sankuai.com/collabpage/2712295141
 */
const getMSI = async () => {
  if (!(env.isHarmony && env.isKNB)) {
    console.log('ℹ️ [MSI] 非鸿蒙系统，不使用msi')
    return null
  }

  // 如果已经加载过，直接返回
  if (msiInstance) {
    return msiInstance
  }

  // 如果正在加载中，等待加载完成
  if (msiLoadPromise) {
    console.log('⏳ [MSI] 正在加载中，等待...')
    return msiLoadPromise
  }

  // 开始加载 MSI
  msiLoadPromise = (async () => {
    try {
      console.log('📦 [MSI] 动态导入 @mtfe/msi-knb-loader...')
      const { msi } = await import('@mtfe/msi-knb-loader')

      // 尝试获取更详细的鉴权信息
      const loadRes = await msi.load()
      
      console.log('🔍 [MSI] msi 对象详情:', {
        hasLoad: typeof msi.load === 'function',
        hasRequest: typeof msi.request === 'function',
        msiKeys: Object.keys(msi),
        msiPrototype: Object.getPrototypeOf(msi)
      })

      if (loadRes) {
        msiInstance = msi
        return msi
      } else {
        // 获取更详细的鉴权失败信息
        const errorDetail = {
          loadResult: loadRes,
          msiObject: msi,
          windowMsi: typeof window !== 'undefined' ? window.msi : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          location: typeof window !== 'undefined' ? window.location.href : undefined
        }
        console.error('❌ [MSI] 鉴权失败详情:', errorDetail)
        sendRaptorErr('MSI鉴权失败', errorDetail, 'customError')

        // 重置 Promise，允许重试
        msiLoadPromise = null
        return null
      }
    } catch (error) {
      console.error('❌ [MSI] 导入或初始化失败:', error)
      sendRaptorErr('MSI导入或初始化失败', error, 'customError')

      // 重置 Promise，允许重试
      msiLoadPromise = null
      return null
    }
  })()

  return msiLoadPromise
}

// 日志和错误发送函数
const logAndSendError = (message, error, type) => {
  console.warn(message, error)
  sendRaptorErr(message, error, type)
}

// 处理请求错误
const handleRequestError = (err) => {
  logAndSendError('请求异常', err, 'ajaxError')
  return Promise.reject(err)
}

// 处理响应错误
const handleResponseError = (response) => {
  logAndSendError('响应异常', response, 'ajaxError')
  toast.show({ type: 'text', text: response.statusText })
  return Promise.reject(response.statusText)
}

// 处理服务器错误
const handleServerError = (response) => {
  logAndSendError('服务端响应异常', response, 'ajaxError')
  const code = response.data.error.code
  const errorHandlers = {
    20001: () => {
      Login()
      return Promise.reject({ code, message: '请重新登录' })
    },
    30001: () => {
      Login()
      return Promise.reject({ code, message: '请重新登录' })
    },
    300110: () => Promise.reject({ code }),
    300301: () => Promise.reject({ code }),
    300302: () => Promise.reject({ code }),
    300104: () => Promise.reject({ code }),
    default: () => Promise.reject(response.data.error),
  }
  return (errorHandlers[code] || errorHandlers.default)()
}

// 🟩 【新增】使用 MSI 发送请求
// 参考文档：https://msi-extend.sankuai.com/api?tab=C&id=1539
const sendRequestWithMSI = (msi, config) => {
  return new Promise((resolve, reject) => {
    try {
      const baseURL = config.baseURL || Ajax.defaults.baseURL

      // 使用 URL 构造函数拼接 URL（自动处理斜杠和完整 URL）
      const fullUrl = new URL(config.url, baseURL).href
      // 使用 window.msi 而不是导入的 msi 对象
      const msiRequest = typeof window !== 'undefined' && window.msi ? window.msi : msi
      
      const msiHeaders = {
        'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
        'user-agent': navigator.userAgent || '', // 鸿蒙KNB容器在美团12.48.400以下header中未添加ua公参，需要手动添加
        ...config.headers
      }
      console.log('🔍 [MSI] 请求配置:', {
        url: fullUrl,
        method: config.method || 'POST',
        headers: msiHeaders,
        timeout: config.timeout || 6000,
        withCredentials: true
      })

      // 调用 MSI 的 request 方法
      // 基建网络请求会自动在 header 中带上 mtgsig 签名
      msiRequest.request({
        url: fullUrl,
        method: config.method || 'POST',
        header: msiHeaders,
        data: config.data,
        timeout: config.timeout || 6000,
        success: (response) => {
          console.log('📥 [MSI] 收到响应:', response.data)

          if (response.data.status !== 'success') {
            handleServerError(response)
              .catch((err) => {
                if (
                  err.message === 'Network Error' ||
                  err.message.startsWith('timeout of')
                ) {
                  logAndSendError('网络异常/请求超时', err, 'customError')
                } else {
                  logAndSendError('未知响应异常', err, 'customError')
                }
                reject({ code: 100000, message: '断网环境' })
            })
            return
          }
          resolve(response.data.data)
        },
        fail: (error) => {
          console.error('❌ [MSI] 请求失败:', {
            error: error,
            message: error.message || error,
            timestamp: new Date().toISOString()
          })
          reject({
            code: 100000,
            message: '网络请求失败',
            error,
          })
        }
      })
    } catch (error) {
      console.error('❌ [MSI] 请求失败:', error)
      reject(error)
    }
  })
}

// 请求拦截器
Ajax.interceptors.request.use(
  async (config) => {
    try {
      const commonParams = await getCommonParams()
      if (config.url === '/qrcode/offline/getseed') {
        localStorage.setItem('AESkey', commonParams.AESKey)
      }
      config.data = qs.stringify(Object.assign(commonParams, config.data))
    } catch (e) {
      if (config.url === '/qrcode/offline/getseed') {
        localStorage.setItem('AESkey', e.AESKey)
      }
      config.data = qs.stringify(Object.assign(e, config.data))
    }
    return config
  },
  handleRequestError
)

// 响应拦截器
Ajax.interceptors.response.use(
  (response) => {
    if (response.status !== 200) {
      return handleResponseError(response)
    }
    if (response.data.status !== 'success') {
      return handleServerError(response)
    }
    return response.data.data
  },
  (err) => {
    if (
      err.message === 'Network Error' ||
      err.message.startsWith('timeout of')
    ) {
      logAndSendError('网络异常/请求超时', err, 'ajaxError')
    } else {
      logAndSendError('未知响应异常', err, 'ajaxError')
    }
    return Promise.reject({ code: 100000, message: '断网环境' })
  }
)

// 🟩 【修改】使用 if-else 替代 Proxy，避免浏览器兼容性问题
const AjaxWrapper = async (config = {}) => {
  // 鸿蒙系统下使用 MSI
  if (env.isHarmony && env.isKNB) {
    try {
      console.log('🔍 [AjaxWrapper] 检测到鸿蒙系统，尝试使用 MSI')
      const msi = await getMSI()
      if (msi) {
        // 获取公共参数
        const commonParams = await getCommonParams()
        if (config.url === '/qrcode/offline/getseed') {
          localStorage.setItem('AESkey', commonParams.AESKey)
        }

        // 合并数据
        const mergedData = Object.assign(commonParams, config.data)

        // 使用 MSI 发送请求
        return await sendRequestWithMSI(msi, {
          ...config,
          data: mergedData,
        })
      } else {
        sendRaptorErr('MSI导入或初始化失败', {}, 'customError')
        console.error('⚠️ [AjaxWrapper] MSI 不可用，不降级到 axios，直接抛出错误')
        throw { code: 100000, message: 'MSI 不可用' }
      }
    } catch (error) {
      sendRaptorErr('MSI请求异常', error, 'customError')
      console.error('❌ [AjaxWrapper] MSI 请求失败，不降级到 axios:', error)
      // 不降级到 axios，直接抛出错误
      throw error
    }
  } else {
    console.log('ℹ️ [AjaxWrapper] 非鸿蒙系统，使用 axios')
    // 其他系统使用 axios
    return Ajax(config)
  }
}

export default AjaxWrapper
